import {
  booleanAttribute,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  numberAttribute,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  WritableSignal,
  inject,
} from '@angular/core';
import {
  ColumnKeys,
  ExceptionRules,
  ExpandMode,
  FirstRowConfig,
  Item,
  PaginationConfig,
  PlaceholderConfig,
  RowsExceptionRules,
  SelectInputConfig,
  TableColumn,
  TooltipConfig,
  UpdateTableData,
} from './table.utils';
import { NgZone } from '@angular/core';
import { ChangeDetectionStrategy } from '@angular/core';
import { SharedService } from '../../shared.service';
import { NzTableComponent, NzThMeasureDirective, NzCellEllipsisDirective } from 'ng-zorro-antd/table';
import { NgClass, NgTemplateOutlet, NgStyle, PercentPipe } from '@angular/common';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { FormsModule } from '@angular/forms';
import { NumbersValidationDirective } from '../../directives/numbers-validation.directive';
import { SuffixDirective } from '../../directives/suffix.directive';
import { NzSwitchComponent } from 'ng-zorro-antd/switch';
import { NzSpaceCompactItemDirective } from 'ng-zorro-antd/space';
import { NzSelectComponent, NzOptionComponent } from 'ng-zorro-antd/select';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { TranslatePipe } from '@ngx-translate/core';

let updateDataTriggerCount = 1;

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NzTableComponent,
    NgClass,
    NzThMeasureDirective,
    NzCellEllipsisDirective,
    NgTemplateOutlet,
    NgStyle,
    TooltipDirective,
    FormsModule,
    NumbersValidationDirective,
    SuffixDirective,
    NzSwitchComponent,
    NzSpaceCompactItemDirective,
    NzSelectComponent,
    NzOptionComponent,
    NzTooltipDirective,
    PercentPipe,
    TranslatePipe,
  ],
})
export class TableComponent implements OnChanges {
  private sharedService = inject(SharedService);
  private cdr = inject(ChangeDetectorRef);
  private zone = inject(NgZone);

  protected disableSelectBehavior = false;
  /**
   * tableData is a signal that contains a shadow copy of the `data` @Input.
   * It is used to store the table data and is updated when the `data` input changes.
   * @type {WritableSignal<any[]>}
   */
  tableData: WritableSignal<any[]> = signal([]);
  /**
   * The header of the table component.
   * @type {HTMLElement|string}
   */
  @Input({ transform: numberAttribute }) tableHeaderGroupHeight: number = 0;
  @Input() tableHeader: string;
  @Input({ required: true }) data: any[];
  @Input({ required: true }) columns: TableColumn[];
  @Input({ required: true, transform: numberAttribute }) totalCount: number;
  @Input({ required: true, transform: numberAttribute }) pageIndex: number;
  @Input({ required: true, transform: numberAttribute }) pageSize: number;
  @Input({ transform: booleanAttribute }) expandableCollapsibleRows: boolean = false;

  /*
   * This is the configuration for the first row of the table
   * */
  /**
   * The table header groups exist.
   * Use it only when you want to add a header group to the table.
   * @type {boolean}
   */
  @Input({ transform: booleanAttribute }) tableHeaderGroupsExist: boolean = false;
  /*
   * This is the configuration for the first row of the table
   * */
  @Input() firstRowConfig: FirstRowConfig = null;
  /*
   * This is to add right border to the group/collection of columns <br>
   * Is an array `number[]` of the column index/offset after which column (index) should border to be added
   * */
  @Input() addRightBorderForColumnCollection: number[] = [];
  /*
   * Tooltip configuration for the table (show or hide the tooltip, showOnColumnIdx, dataProperty)
   * */
  @Input() tooltipConfig: TooltipConfig[] = [];
  /*
   * Updated table data event after editing the input in the table
   * */
  @Output() tableDataChange = new EventEmitter<UpdateTableData<any>>();
  /**
   * Handles the cancellation or confirmation of table data changes.
   * @emits {UpdateTableData<any>} tableDataChange - If the changes were confirmed.
   * @emits {UpdateTableData<any>} onBlurOTableData - If the changes were cancelled.
   */
  @Output() cancelOrConfirmTableData = new EventEmitter<UpdateTableData<any>>();
  /*
   * This meant only if you have an inputs in your table <br>
   * `showOnColumnIdx` array of column index on which column(s) the placeholder should be shown <br>
   * `text` text that should be shown as placeholder
   * */
  @Input() placeholderConfig: PlaceholderConfig[] = [];
  /*
   * This is for select input configuration <br>
   * `columnIdx` array of column index on which column(s) the select input should be shown <br>
   * `options` options that should be shown (the list) in the select input
   * */
  @Input() selectInputConfig: SelectInputConfig[] = [];
  /*
   * Sorting configuration (sortKey, sortDirection)
   * */
  //  @Input() sortConfig: SortConfig;
  /*
   * Pagination change event with pagination config (page, size, sort, direction)
   * */
  @Output() paginationChange = new EventEmitter<PaginationConfig>();
  /*
   * Add row exception rules <br>
   * First: add `rowIdx` row index to point the row to which exception rules should be applied <br>
   * Second: add `exceptionRules` array of exception rules <br>
   * Third: add `columnIdx` column index to point the column to which exception rule should be applied <br>
   * Forth: add `rules` array of `ExceptionRules` interface (value, type, hide) [please take a look on the interface]
   * */
  @Input() rowsExceptionRules: RowsExceptionRules[];
  /*
   * This used when you want to color the cells in a specific column <br>
   * Just pass the column key in the `coloredCellsColumnsKey`
   * */
  @Input() coloredCellsColumnsKey: string[];
  @Input() validateCellsColumnsKey: string[];
  @Input() validateCellsRows: number[];
  /**
   * Event emitted when a row is selected. ONLY IF 'selected' property is true and exist in the tableData.
   * @event selectRow
   * @type {EventEmitter<{}>}
   * @description Emits the selected row data and a boolean indicating whether the row is selected or deselected.
   */
  @Output() selectRow = new EventEmitter<Record<string, any>>();
  /**
   * Event emitted when the sorting order of a column is changed.
   * @event sorting
   * @type {EventEmitter<TableColumn>}
   * @description Emits the column data when the sorting order is changed.
   */
  @Output() sorting: EventEmitter<TableColumn> = new EventEmitter<TableColumn>();
  /**
   * Event emitted when a link type cell is clicked.
   * @event linkTypeCellClick
   * @type {EventEmitter<{}>}
   * @description Emits an event when a link type cell is clicked, passing the cell data.
   */
  @Output() linkTypeCellClick = new EventEmitter<Record<string, any>>();
  @Input() sortKey: ColumnKeys | null = null;
  @Input() isReset: boolean = false;

  isSortReset = false;
  expandedRows: string[] = [];
  expandMode: WritableSignal<ExpandMode> = signal('manual');

  ngOnChanges(changes: SimpleChanges) {
    const isReset = changes['isReset']?.currentValue === true;
    const dataChanged = !!changes['data'];
    if (isReset) {
      this.expandedRows = [];
      this.expandMode.set('collapsed');
    }
    if (dataChanged) {
      this.tableData.set(
        this.data.map((row) => ({
          ...row,
          collapsed: this.getRowCollapsedState(row.productId),
        })),
      );
    }
    if (changes['sortKey']) {
      this.sortKey = changes['sortKey'].currentValue;
      this.isSortReset = !this.sortKey;
      this.cdr.markForCheck();
    }
  }

  changePageIndex(value: number): void {
    this.pageIndex = value;
    this.paginationChange.emit({ page: value, size: this.pageSize });
  }

  onSort(column: TableColumn) {
    this.sorting.emit(column);
    this.sortKey = column.id;
  }

  handleTableDataChange(rowIdx: number, columnName: string, value: any, subrowIdx: number, ignoreTriggerCount = false) {
    // `updateDataTriggerCount` ensures that `tableDataChange` is emitted only once.
    // Enter triggers blur, so without this guard both handlers would emit the same change.
    if (updateDataTriggerCount === 1 && !ignoreTriggerCount) {
      this.tableDataChange.emit({ rowIdx, columnName, value, tableData: this.tableData(), subrowIdx });
      --updateDataTriggerCount;
    }

    if (ignoreTriggerCount) {
      this.tableDataChange.emit({ rowIdx, columnName, value, tableData: this.tableData(), subrowIdx });
    }
  }

  handleCancelOrConfirmTableData(rowIdx: number, columnName: string, value: any, subrowIdx: number) {
    if (updateDataTriggerCount === 1) {
      this.cancelOrConfirmTableData.emit({ rowIdx, columnName, value, subrowIdx, tableData: this.tableData() });
      --updateDataTriggerCount;
    }
  }

  onSelectRowHandler(row: Record<string, any>, productId: string) {
    row['productId'] = productId;
    this.selectRow.emit(row);
  }

  getRowTooltipConfig(columnIdx: number): TooltipConfig | null {
    return this.tooltipConfig?.find((config) => config.showOnColumnIdx === columnIdx);
  }

  getRowPlaceholderConfig(columnIdx: number): string | null {
    return this.placeholderConfig?.find((config) => config.showOnColumnIdx === columnIdx)?.text;
  }

  getRowSelectInputConfig(columnIdx: number): Item[] | null {
    return this.selectInputConfig?.find((config) => config.columnIdx === columnIdx)?.options;
  }

  getRowExceptionRules(rowIdx: number, columnIdx: number): ExceptionRules | null {
    return this.rowsExceptionRules
      ?.find((config) => config.rowIdx === rowIdx)
      ?.exceptionRules.find((rules) => rules.columnIdx === columnIdx);
  }

  handleOnMouseoverInputField() {
    this.disableSelectBehavior = true;
  }

  handleOnMouseleaveInputField() {
    this.disableSelectBehavior = false;
  }

  resetUpdateDataTriggerCount() {
    updateDataTriggerCount = 1;
  }

  handleOnLinkTypeCellClick(row: Record<string, any>, parentRow) {
    row['productName'] = parentRow['productName'];
    row['duration'] = parentRow['duration'];
    row['productId'] = parentRow['productId'];
    this.linkTypeCellClick.emit(row);
  }

  private getRowCollapsedState(productId: string): boolean {
    const mode = this.expandMode();
    if (mode === 'expanded') {
      return false;
    }
    if (mode === 'collapsed') {
      return true;
    }
    return !this.expandedRows.includes(productId);
  }

  public toggleExpandAll(): void {
    this.sharedService.showLoader();
    this.cdr.detectChanges();

    setTimeout(() => {
      const currentMode = this.expandMode();

      if (currentMode === 'expanded') {
        this.expandMode.set('manual');
      } else {
        this.expandMode.set('expanded');
        this.expandedRows = []; // Clear manual tracking
      }

      this.tableData.update((data) =>
        data.map((r) => ({
          ...r,
          collapsed: this.getRowCollapsedState(r.productId),
        })),
      );

      this.cdr.detectChanges();
      this.sharedService.hideLoader();
    }, 0);
  }

  public toggleCollapseAll(): void {
    this.sharedService.showLoader();
    this.cdr.detectChanges();

    setTimeout(() => {
      const currentMode = this.expandMode();

      if (currentMode === 'collapsed') {
        this.expandMode.set('manual');
      } else {
        this.expandMode.set('collapsed');
        this.expandedRows = []; // Clear manual tracking
      }
      this.tableData.update((data) =>
        data.map((r) => ({
          ...r,
          collapsed: this.getRowCollapsedState(r.productId),
        })),
      );

      this.cdr.detectChanges();
      this.sharedService.hideLoader();
    }, 0);
  }

  protected tableScrollYCalculationCss() {
    return this.tableHeader?.length ? 'calc(100vh - 350px)' : `calc(100vh - ${350 - 48}px)`;
  }

  protected getTheNameOfTheItemFromSelectInputList(columnIdx: number, value: string): string {
    return this.getRowSelectInputConfig(columnIdx)?.find((item) => item.value === value)?.label;
  }

  protected handleExpandCollapseChange(event: MouseEvent, rowIdx: number): void {
    event.stopPropagation();

    const currentRow = this.tableData()[rowIdx];
    const productId = currentRow.productId;
    const willBeCollapsed = !currentRow.collapsed;

    // Force manual mode when the user toggles a single row
    if (this.expandMode() !== 'manual') {
      this.expandMode.set('manual');
    }

    // Maintain explicit list of expanded rows for manual mode
    if (willBeCollapsed) {
      // collapsing -> remove from expanded list
      this.expandedRows = this.expandedRows.filter((id) => id !== productId);
    } else {
      // expanding -> ensure in expanded list
      if (!this.expandedRows.includes(productId)) {
        this.expandedRows.push(productId);
      }
    }

    // Update just this row’s collapsed state
    this.tableData.update((data) => {
      const newData = [...data];
      newData[rowIdx] = { ...newData[rowIdx], collapsed: willBeCollapsed };
      return newData;
    });
  }
}
